/**
 * Downscale + re-encode an image file to a data URL that comfortably fits under
 * MongoDB document and upload-size limits. Handles the common case of modern
 * phone photos (4–12 MB HEIC/JPEG) that previously produced a "Couldn't upload
 * picture" error at the backend.
 *
 * @param {File} file  Original image file from an <input type="file">.
 * @param {object} [opts]
 * @param {number} [opts.maxDim=1024]  Max width or height in pixels.
 * @param {number} [opts.quality=0.85] JPEG quality 0..1.
 * @returns {Promise<string>} data URL (data:image/jpeg;base64,…)
 */
export async function resizeImageToDataUrl(file, { maxDim = 1024, quality = 0.85 } = {}) {
  const bitmap = await createImageBitmap(file).catch(async () => {
    // Fallback path for browsers that fail to decode HEIC via createImageBitmap
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  });
  const w = bitmap.width;
  const h = bitmap.height;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const targetW = Math.round(w * scale);
  const targetH = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  return canvas.toDataURL("image/jpeg", quality);
}
